import * as yup from 'yup';
import { GamifyToast } from 'application/common/CustomToasts';

export const aboutValidationSchema = yup.object().shape({
  name: yup
    .string()
    .label('Name')
    .required()
    .min(3, 'The name is too short.')
    .max(1024, 'The name is too long.'),

  description: yup
    .string()
    .label('Description')
    .max(
      4096,
      'The description is too long. Please use less than 4096 characters'
    ),
  endDate: yup
    .string()
    .nullable(true)
    .when('contestType', {
      is: (value: any) => value && !value.includes('bracket'),
      then: yup
        .string()
        .nullable(true) // Allow null when required
        .required('End date is required')
        .test(
          'is-valid-date',
          'End date must be a valid date or null',
          (value) =>
            value === null || !value ? false : !isNaN(Date.parse(value))
        ),
      otherwise: yup.string().nullable(true).notRequired(),
    }),
});

export const step2ValidationSchema = yup.object({
  KPIRuleID: yup.string().when('contestType', {
    is: (value: any) => value === 'incentive',
    then: (schema) => schema.notRequired(),
    otherwise: (schema) => schema.required('KPI is required'),
  }),
  rules: yup.mixed().when('contestType', {
    is: (value: any) => value === 'incentive',
    then: () =>
      yup
        .array()
        .of(
          yup.object({
            id: yup.string().required('KPI is required'),
            goal: yup
              .number()
              .typeError('Goal must be a number')
              .min(1, 'Goal must be > 0')
              .required('Goal is required'),
            multiplier: yup
              .number()
              .typeError('Multiplier must be a number')
              .min(0)
              .nullable(true),
            role: yup.string().nullable(true),
          })
        )
        .min(1, 'Add at least one KPI rule')
        .required(),
    otherwise: () => yup.mixed().notRequired(),
  }),
  rewardCount1: yup.mixed().when(['contestType', 'rewardCurrency'], {
    is: (contestType: string, rewardCurrency: string) =>
      contestType === 'incentive' && rewardCurrency === 'points',
    then: () =>
      yup
        .number()
        .typeError('Points amount must be a number')
        .min(1, 'Points must be > 0')
        .required('Points amount is required'),
    otherwise: () => yup.mixed().notRequired(),
  }),
});

export const bracketStep2ValidationSchema = yup.object({
  currentTotalCompetitors: yup
    .number()
    .required('Total competitors is required'),
  bracketName: yup.string().required('Round name is required'),
  KPIRuleID: yup.string().required('KPI is required'),
  endDate: yup.mixed().when(['contestType'], {
    is: (contestType: string) =>
      contestType &&
      contestType.includes('bracket') &&
      contestType !== 'bracket_manual',
    then: () => yup.date().required('End date is required'),
    otherwise: () => yup.string().nullable(true).notRequired(),
  }),
});

export const step3ValidationSchema = yup.object({
  assigneeIDs: yup.array().when('contestType', {
    is: (contestType: string) => contestType?.includes('bracket'),
    then: (schema) => schema.notRequired(),
    otherwise: (schema) =>
      schema
        .of(yup.string().required('Contestants are required'))
        .min(1, 'At least one contestant is required')
        .required('Contestants are required'),
  }),
});

// Helper function to display toast when required fields are missing
export const showRequiredFieldsToast = () => {
  GamifyToast.error(
    'Required fields',
    'Please fill in the required fields before proceeding'
  );
};

// Validate the current step based on active step
export const validateStep = async (gameState: any, activeStep: number) => {
  // For bracket validation, perform comprehensive state syncing and validation
  if (activeStep === 1 && gameState.contestType?.includes('bracket')) {
    // Comprehensive pre-validation syncing - ensure all main fields are set from rounds
    if (gameState.bracket?.roundValues?.length > 0) {
      const rounds = gameState.bracket.roundValues;
      const firstRound = rounds[0];
      const lastRound = gameState.totalRounds
        ? rounds[gameState.totalRounds - 1]
        : null;

      // Sync bracketName from first round
      if (!gameState.bracketName && firstRound?.bracketName) {
        gameState.bracketName = firstRound.bracketName;
      }

      // Sync KPIRuleID from last round
      if (!gameState.KPIRuleID && lastRound?.KPIRuleID) {
        gameState.KPIRuleID = lastRound.KPIRuleID;
      }

      // Sync dates from rounds
      if (!gameState.startDate && firstRound?.startDate) {
        gameState.startDate = firstRound.startDate;
      }

      if (!gameState.endDate && lastRound?.endDate) {
        gameState.endDate = lastRound.endDate;
      }

      // Note: currentTotalCompetitors is handled in the validation call below
    }

    // Pre-validation check - validate all rounds have required fields
    if (gameState.bracket?.roundValues && gameState.totalRounds) {
      const rounds = gameState.bracket.roundValues;

      // Check if we have the correct number of rounds
      if (rounds.length < gameState.totalRounds) {
        return {
          isValid: false,
          errors: {
            bracketSetup: `Please configure all ${gameState.totalRounds} rounds before proceeding`,
          },
        };
      }

      // Check each round for required fields - but only for rounds that should be configured
      // If user is on the last round and wants to proceed, be more lenient about incomplete rounds
      const isOnLastRound = gameState.currentRound === gameState.totalRounds;
      const checksToPerform = isOnLastRound
        ? gameState.totalRounds
        : gameState.currentRound || gameState.totalRounds;

      for (let i = 0; i < checksToPerform; i++) {
        const round = rounds[i];

        // Validate round has a name
        if (!round?.bracketName) {
          console.log(
            `[BRACKET-VALIDATION-ERROR] Missing bracketName for round ${i + 1}`
          );
          return {
            isValid: false,
            errors: { bracketName: `Round ${i + 1} name is required` },
          };
        }

        // Validate round has a KPI
        if (!round?.KPIRuleID) {
          console.log(
            `[BRACKET-VALIDATION-ERROR] Missing KPIRuleID for round ${i + 1}`,
            {
              roundData: {
                bracketName: round?.bracketName,
                KPIRuleID: round?.KPIRuleID,
                KPIRuleName: round?.KPIRuleName,
                startDate: round?.startDate,
                endDate: round?.endDate,
              },
            }
          );
          return {
            isValid: false,
            errors: { KPIRuleID: `Round ${i + 1} KPI is required` },
          };
        }

        // Validate round has start and end dates
        if (!round?.startDate) {
          console.log(
            `[BRACKET-VALIDATION-ERROR] Missing startDate for round ${i + 1}`
          );
          return {
            isValid: false,
            errors: { startDate: `Round ${i + 1} start date is required` },
          };
        }

        if (!round?.endDate) {
          console.log(
            `[BRACKET-VALIDATION-ERROR] Missing endDate for round ${i + 1}`
          );
          return {
            isValid: false,
            errors: { endDate: `Round ${i + 1} end date is required` },
          };
        }
      }

      // Special case: If user has filled all rounds and is trying to proceed to step 3
      // Check if all rounds are actually complete before doing the full validation
      if (isOnLastRound) {
        const allRoundsComplete = rounds
          .slice(0, gameState.totalRounds)
          .every(
            (round: any) =>
              round?.bracketName &&
              round?.KPIRuleID &&
              round?.startDate &&
              round?.endDate
          );

        if (allRoundsComplete) {
          console.log(
            `[BRACKET-VALIDATION] All rounds complete, allowing progression to step 3`
          );
          // Ensure main state has all required fields synced
          const lastRound = rounds[gameState.totalRounds - 1];
          if (lastRound) {
            if (!gameState.KPIRuleID) gameState.KPIRuleID = lastRound.KPIRuleID;
            if (!gameState.endDate) gameState.endDate = lastRound.endDate;
            if (!gameState.bracketName && rounds[0])
              gameState.bracketName = rounds[0].bracketName;
            if (
              !gameState.currentTotalCompetitors &&
              gameState.bracket?.totalCompetitors
            ) {
              gameState.currentTotalCompetitors =
                gameState.bracket.totalCompetitors;
            }
          }

          // All rounds are complete, bypass yup validation and return success
          return { isValid: true, errors: {} };
        }
      }
    }
  }

  try {
    if (activeStep === 0) {
      await aboutValidationSchema.validate(gameState, { abortEarly: false });
    } else if (activeStep === 1) {
      if (gameState.contestType?.includes('bracket')) {
        // For bracket validation, check if we're editing an existing bracket with competitors
        const isEditMode =
          gameState.id &&
          gameState.competitors &&
          gameState.competitors.length > 0;

        // Check if competitors is an array of strings (IDs) - this is the case for bracket editing
        const hasStringCompetitors =
          gameState.competitors &&
          gameState.competitors.length > 0 &&
          typeof gameState.competitors[0] === 'string';

        await bracketStep2ValidationSchema.validate(
          {
            ...gameState,
            currentTotalCompetitors: gameState.bracket?.totalCompetitors,
            competitors: isEditMode ? gameState.competitors : [],
            isEditMode,
            hasStringCompetitors,
          },
          { abortEarly: false }
        );
      } else {
        await step2ValidationSchema.validate(gameState, { abortEarly: false });
      }
    } else if (activeStep === 2) {
      console.log(`[BRACKET-DEBUG] Step 3 validation`, {
        contestType: gameState.contestType,
        competitorsLength: gameState.competitors?.length || 0,
        assigneeIDsLength: gameState.assigneeIDs?.length || 0,
        hasAssigneeIDs:
          Boolean(gameState.assigneeIDs) &&
          Array.isArray(gameState.assigneeIDs),
      });

      await step3ValidationSchema.validate(gameState, { abortEarly: false });
    }

    return { isValid: true, errors: {} };
  } catch (error) {
    if (error instanceof yup.ValidationError) {
      const validationErrors: Record<string, string> = {};

      error.inner.forEach((err) => {
        if (err.path) {
          validationErrors[err.path] = err.message;
        }
      });

      return {
        isValid: false,
        errors: validationErrors,
      };
    }

    // For other types of errors
    console.error(
      `[BRACKET-DEBUG] Unexpected error during validation for step ${activeStep}`,
      error
    );
    return {
      isValid: false,
      errors: { general: 'An unexpected error occurred' },
    };
  }
};

// Validate and show toast if validation fails
export const validateStepWithToast = async (
  gameState: any,
  activeStep: number
) => {
  const result = await validateStep(gameState, activeStep);

  if (!result.isValid) {
    // Get the first error message to display
    const errorMessages = Object.values(result.errors);
    const errorFields = Object.keys(result.errors);

    if (errorMessages.length > 0) {
      // Show specific error message instead of generic one
      const primaryError = errorMessages[0];
      const fieldName = errorFields[0];

      GamifyToast.error('Required field: ' + fieldName, primaryError);
    } else {
      // Fallback to generic message if no specific errors
      showRequiredFieldsToast();
    }
  }

  return result;
};

interface SchemaFields {
  [key: string]: any;
}

// Helper function to check if a field is required based on schema
export const isFieldRequired = (field: string, activeStep: number) => {
  let schema: yup.AnyObjectSchema;

  switch (activeStep) {
    case 0:
      schema = aboutValidationSchema;
      break;
    case 1:
      schema = step2ValidationSchema;
      break;
    case 2:
      schema = step3ValidationSchema;
      break;
    default:
      return false;
  }

  try {
    // Check if the field has a 'required' test in the schema
    const fields = schema.fields as SchemaFields;
    const fieldSchema = fields[field];
    if (!fieldSchema) return false;

    // This is a simplified way to check if a field is required
    // It might not work for all complex Yup schemas
    return fieldSchema.tests.some((test: any) => test.name === 'required');
  } catch (error) {
    return false;
  }
};
